import React from 'react';
import Header from './Header';
import SEO from './SEO';
import { Heart, Target, Puzzle, Users, Sparkles } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="About PocketHints - Our Story & Mission"
        description="Learn about PocketHints, the ultimate daily puzzle helper. Discover our mission to help you solve Pocket Grids, Syllo, and more without spoilers."
        url={`${window.location.origin}/about`}
      />

      <Header 
        date={new Date().toISOString()} 
        title="About Us" 
        gameName="PocketHints Team" 
        heroTitle={
            <span className="text-gray-900">
            About <span className="text-brand-blue relative inline-block">
                PocketHints
                <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-yellow -z-10 opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                 <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                </svg>
            </span>
            </span>
        }
      />

      <main className="flex-grow max-w-4xl mx-auto px-4 w-full py-12">
        {/* Intro Section */}
        <div className="text-center mb-16">
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            We are a community of puzzle enthusiasts dedicated to keeping your daily streaks alive.
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
            <div className="w-14 h-14 bg-blue-50 text-brand-blue rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Puzzle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-3">Pure Puzzle Love</h3>
            <p className="text-gray-600 leading-relaxed">
              We don't just solve puzzles; we cherish them. Every hint is crafted to preserve the "aha!" moment.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
            <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Target className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-3">No Spoilers First</h3>
            <p className="text-gray-600 leading-relaxed">
              Our progressive hint system ensures you only get the help you need, exactly when you need it.
            </p>
          </div>

          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
            <div className="w-14 h-14 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Users className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-3">Community Driven</h3>
            <p className="text-gray-600 leading-relaxed">
              We listen to the reddit communities of Pocket Grids, Syllo, and others to provide the most useful tools.
            </p>
          </div>
        </div>

        {/* Story Section */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-yellow/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-6 h-6 text-red-500 fill-current" />
              <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight">Why We Built This</h2>
            </div>
            
            <div className="prose prose-lg text-gray-600 max-w-none">
              <p className="mb-6">
                It started with a simple frustration. We loved playing <strong>Pocket Grids</strong>, <strong>Syllo</strong> and other daily Reddit games every morning with our coffee, but getting stuck on a single clue often meant losing a streak or giving up entirely.
              </p>
              <p className="mb-6">
                Searching for answers online was a minefield. Most sites would just blurt out the solution, ruining the satisfaction of solving the puzzle. We wanted something different. We wanted a <em>partner</em> in solving, not a cheat sheet.
              </p>
              <p>
                So, <strong>PocketHints</strong> was born. We built a platform that respects the game and the player. Whether you need a tiny nudge in the right direction or a full explanation of a tricky wordplay, we're here to help you finish what you started.
              </p>
            </div>

            <div className="mt-10 flex items-center gap-4 pt-8 border-t border-gray-100">
               <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center text-white font-bold text-xl">
                 PH
               </div>
               <div>
                 <div className="font-bold text-gray-900">The PocketHints Team</div>
                 <div className="text-sm text-gray-500">Puzzle Solvers & Web Builders</div>
               </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AboutPage;

